import { useShallow } from 'zustand/react/shallow';
import type { GalleryState } from 'components/common/gallery';
import { useDocumentationStore } from '../store';
import { findGalleryFileIndex } from '../slices/gallery/model';
import type { DocumentationDisplayedFile } from '../slices/gallery/types';

/*
 * Gallery context hooks backed by real store selectors. They are module-level
 * (stable identity), so the context value does not change when files or the
 * slide change, and each consumer re-renders only when its selected value
 * changes (compared shallowly). Selectors should return primitives or values
 * taken from the store.
 */

export const setGalleryFiles = (files: DocumentationDisplayedFile[]): void =>
  useDocumentationStore.getState().setFiles(files);

export const showGalleryFile = (file: DocumentationDisplayedFile): void => {
  const { files, navigateToSlide } = useDocumentationStore.getState();
  const index = findGalleryFileIndex(files, file);
  if (index !== -1) {
    navigateToSlide(index);
  }
};

export const setGalleryState = (state: GalleryState): void =>
  useDocumentationStore.getState().setGalleryState(state);

export function useDocumentationDisplayedFiles<T>(
  selector: (files: DocumentationDisplayedFile[]) => T
): [T, (files: DocumentationDisplayedFile[]) => void] {
  const value = useDocumentationStore(useShallow((state) => selector(state.files)));
  return [value, setGalleryFiles];
}

export function useDocumentationMainDisplayedFile<T>(
  selector: (file: DocumentationDisplayedFile | undefined) => T
): [T, (file: DocumentationDisplayedFile) => void] {
  const value = useDocumentationStore(
    useShallow((state) => selector(state.files[state.currentSlide]))
  );
  return [value, showGalleryFile];
}

export function useDocumentationGalleryState<T>(
  selector: (state: GalleryState) => T
): [T, (state: GalleryState) => void] {
  const value = useDocumentationStore(
    useShallow((state) => selector({ open: state.galleryOpen, slide: state.currentSlide }))
  );
  return [value, setGalleryState];
}
